export type DesktopCacheKind = 'bootstrap' | 'list' | 'detail' | 'edition';
type RecordValue = {
  id: string;
  partition: string;
  key: string;
  value: unknown;
  kind: DesktopCacheKind;
  updatedAt: number;
  size: number;
  schemaVersion: 1;
};
export const DESKTOP_CACHE_LIMIT = 100 * 1024 * 1024;
export const DESKTOP_CACHE_MAX_AGE = 30 * 24 * 60 * 60 * 1000;
export const DESKTOP_CACHE_MAX_ENTRIES = 200;
let partition = '';
let generation = 0;
let accountGeneration = 0;
let database: Promise<IDBDatabase | null> | undefined;
export const DESKTOP_CACHE_BLOCKED_PREFIX = 'kiipu.desktop.cache-blocked.v1:';
export const DESKTOP_CACHE_REVISION_PREFIX = 'kiipu.desktop.cache-revision.v1:';
export const DESKTOP_CACHE_CHANGED = 'kiipu:desktop-reading-cache-changed';
const DENIED_PREFIX = 'kiipu.desktop.cache-denied.v1:';
const deniedResources = new Set<string>();
const blockedPartitions = new Set<string>();
const revisions = new Map<string, string>();
function cacheRevision(owner: string) {
  try {
    return (
      localStorage.getItem(DESKTOP_CACHE_REVISION_PREFIX + encodeURIComponent(owner)) ?? 'initial'
    );
  } catch {
    return revisions.get(owner) ?? 'initial';
  }
}
function advanceRevision(owner: string) {
  const value = crypto.randomUUID();
  revisions.set(owner, value);
  try {
    localStorage.setItem(DESKTOP_CACHE_REVISION_PREFIX + encodeURIComponent(owner), value);
  } catch {
    /* Memory still invalidates this window. */
  }
}
function blockPartition(owner: string, blocked: boolean) {
  if (blocked) blockedPartitions.add(owner);
  else blockedPartitions.delete(owner);
  try {
    const key = DESKTOP_CACHE_BLOCKED_PREFIX + encodeURIComponent(owner);
    if (blocked) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    // Memory still fails closed when browser storage is unavailable.
  }
}
function partitionBlocked(owner: string) {
  if (blockedPartitions.has(owner)) return true;
  try {
    return localStorage.getItem(DESKTOP_CACHE_BLOCKED_PREFIX + encodeURIComponent(owner)) === '1';
  } catch {
    return false;
  }
}

export function desktopCacheIdentity() {
  return `${generation}:${partition}:${cacheRevision(partition)}`;
}
export const desktopCachePartition = () => partition;
export function ownsDesktopCacheStorageKey(key: string | null) {
  return Boolean(
    partition &&
      key &&
      [DESKTOP_CACHE_REVISION_PREFIX, DESKTOP_CACHE_BLOCKED_PREFIX].some(
        (prefix) => key === prefix + encodeURIComponent(partition),
      ),
  );
}
function deniedKey(key: string) {
  return (
    DENIED_PREFIX + encodeURIComponent(partition) + ':' + encodeURIComponent(key.split('?')[0]!)
  );
}
/** Repeated 404s from other windows must not start a cache-clear/refetch feedback loop. */
export async function revokeDesktopCacheResource(key: string) {
  if (!partition) return;
  const marker = deniedKey(key);
  try {
    if (localStorage.getItem(marker) === '1') return;
  } catch {
    /* Memory is authoritative in this renderer. */
  }
  if (deniedResources.has(marker)) return;
  deniedResources.add(marker);
  try {
    localStorage.setItem(marker, '1');
  } catch {
    /* Continue to fail closed locally. */
  }
  await clearDesktopCache();
}
function authorizeResource(key: string) {
  const marker = deniedKey(key);
  deniedResources.delete(marker);
  try {
    localStorage.removeItem(marker);
  } catch {
    /* Local storage can be unavailable. */
  }
}
export function desktopCacheAccountIdentity() {
  return `${accountGeneration}:${partition}`;
}
export function initializeDesktopCache(origin: string, userId: string) {
  const next = JSON.stringify([origin, userId]);
  if (next !== partition) {
    partition = next;
    generation++;
    accountGeneration++;
  }
}
export function deactivateDesktopCache() {
  partition = '';
  generation++;
  accountGeneration++;
}
function db(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  database ??= new Promise((resolve) => {
    const request = indexedDB.open('kiipu-desktop-reading-v1', 1);
    request.onupgradeneeded = () => {
      const store = request.result.createObjectStore('snapshots', { keyPath: 'id' });
      store.createIndex('partition', 'partition');
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => {
        request.result.close();
        database = undefined;
      };
      resolve(request.result);
    };
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
  return database;
}
function result<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error('The reading cache could not be read.'));
  });
}
function done(transaction: IDBTransaction) {
  const completion = new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = transaction.onabort = () =>
      reject(transaction.error ?? new Error('The reading cache transaction was interrupted.'));
  });
  // Observe aborts immediately, including when a store operation throws before its await.
  void completion.catch(() => undefined);
  return completion;
}
export function desktopCacheKind(path: string): DesktopCacheKind | null {
  if (!path.startsWith('/api/')) return null;
  const url = new URL(path, 'https://desktop.invalid');
  const pathname = url.pathname;
  if (['/api/v1/folders', '/api/v1/tags', '/api/v1/settings'].includes(pathname))
    return 'bootstrap';
  if (
    pathname === '/api/v1/site-favicons' &&
    url.searchParams.getAll('host').length > 0 &&
    [...url.searchParams.keys()].every((key) => key === 'host')
  )
    return 'bootstrap';
  if (pathname === '/api/v1/items' && !url.searchParams.get('q')) return 'list';
  if (/^\/api\/v1\/items\/[^/]+$/.test(pathname)) return 'detail';
  if (/^\/api\/v1\/items\/[^/]+\/snapshots$/.test(pathname)) return 'edition';
  return null;
}
export async function readDesktopCache<T>(key: string): Promise<T | null> {
  const owner = partition;
  const identity = desktopCacheIdentity();
  if (!owner || partitionBlocked(owner)) return null;
  try {
    const storage = await db();
    if (!storage) return null;
    const value = await result<RecordValue | undefined>(
      storage.transaction('snapshots').objectStore('snapshots').get(`${owner}:${key}`),
    );
    if (
      owner !== partition ||
      identity !== desktopCacheIdentity() ||
      partitionBlocked(owner) ||
      !value ||
      value.schemaVersion !== 1 ||
      value.updatedAt < Date.now() - DESKTOP_CACHE_MAX_AGE
    )
      return null;
    return value.value as T;
  } catch {
    return null;
  }
}
let writeQueue = Promise.resolve();
export function writeDesktopCache(
  key: string,
  value: unknown,
  kind: DesktopCacheKind = 'detail',
): Promise<void> {
  const owner = partition;
  const identity = desktopCacheIdentity();
  if (!owner || partitionBlocked(owner)) return Promise.resolve();
  writeQueue = writeQueue
    .catch(() => undefined)
    .then(async () => {
      const serialized = JSON.stringify(value);
      const size = new TextEncoder().encode(serialized).byteLength;
      if (
        size > DESKTOP_CACHE_LIMIT ||
        owner !== partition ||
        identity !== desktopCacheIdentity() ||
        partitionBlocked(owner)
      )
        return;
      const storage = await db();
      if (
        !storage ||
        owner !== partition ||
        identity !== desktopCacheIdentity() ||
        partitionBlocked(owner)
      )
        return;
      const transaction = storage.transaction('snapshots', 'readwrite');
      const completed = done(transaction);
      const store = transaction.objectStore('snapshots');
      const record: RecordValue = {
        id: `${owner}:${key}`,
        partition: owner,
        key,
        value,
        kind,
        updatedAt: Date.now(),
        size,
        schemaVersion: 1,
      };
      authorizeResource(key);
      store.put(record);
      const records = await result<RecordValue[]>(store.index('partition').getAll(owner));
      let bytes = 0;
      let details = 0;
      for (const entry of records.sort((a, b) => b.updatedAt - a.updatedAt)) {
        bytes += entry.size;
        if (entry.kind === 'detail') details++;
        if (
          entry.updatedAt < Date.now() - DESKTOP_CACHE_MAX_AGE ||
          bytes > DESKTOP_CACHE_LIMIT ||
          (entry.kind === 'detail' && details > DESKTOP_CACHE_MAX_ENTRIES)
        )
          store.delete(entry.id);
      }
      await completed;
    })
    .catch(() => undefined);
  return writeQueue;
}
export async function deleteDesktopCache(key: string) {
  const owner = partition;
  try {
    const storage = await db();
    if (!storage || !owner) return;
    const transaction = storage.transaction('snapshots', 'readwrite');
    const completed = done(transaction);
    transaction.objectStore('snapshots').delete(`${owner}:${key}`);
    await completed;
  } catch {
    blockPartition(owner, true);
  }
}
export async function clearDesktopCache() {
  const owner = partition;
  generation++;
  if (!owner) return;
  advanceRevision(owner);
  // Persist invalidation before deletion: a failed transaction or process exit must
  // not make a known restricted snapshot available at the next offline startup.
  blockPartition(owner, true);
  try {
    const storage = await db();
    if (!storage) throw new Error('Reading cache storage is unavailable.');
    const transaction = storage.transaction('snapshots', 'readwrite');
    const completed = done(transaction);
    const store = transaction.objectStore('snapshots');
    const keys = await result<IDBValidKey[]>(store.index('partition').getAllKeys(owner));
    keys.forEach((key) => store.delete(key));
    await completed;
    blockPartition(owner, false);
  } catch {
    throw new Error(
      'The reading cache could not be cleared. Offline reading is disabled until clearing succeeds.',
    );
  } finally {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function')
      window.dispatchEvent(
        new CustomEvent(DESKTOP_CACHE_CHANGED, { detail: { partition: owner } }),
      );
  }
}
export async function desktopCacheStats() {
  try {
    const identity = desktopCacheIdentity();
    const storage = await db();
    if (!storage || !partition || partitionBlocked(partition)) return { bytes: 0, entries: 0 };
    const records = await result<RecordValue[]>(
      storage
        .transaction('snapshots')
        .objectStore('snapshots')
        .index('partition')
        .getAll(partition),
    );
    if (identity !== desktopCacheIdentity() || partitionBlocked(partition))
      return { bytes: 0, entries: 0 };
    return {
      bytes: records.reduce((total, item) => total + item.size, 0),
      entries: records.filter((item) => item.kind === 'detail').length,
    };
  } catch {
    return { bytes: 0, entries: 0 };
  }
}
